import _ from 'lodash';
import reactiveCashflow from '@riseupil/reactive-cashflow';
import { DateLocales } from '@riseupil/common-utils';
import DDLogs from '@/DDLogs';

// The cashflow actions done (by the customer in elsa or by the cashflow assistant) that a cashflow version may not include yet.
// An action is fully applied only when the next cashflow version runs on the customer. Until then elsa shows it with reactive-cashflow
// (optimus), and the assistant applies these actions on the data it reads, so both know about the actions done in either of them.
// An action is { actionType: <optimus action name>, actionParams: <its params>, referenceVersion: <the cashflow version it was done on -
// the current budget's `created`> }. An action done on a version older than the one loaded is already in it, so it is dropped.
// Kept in localStorage next to the budget cache (riseup.budgets), with the same expiration, for the signed-in customer only: the list is
// stored with the customer's id, and a list of another customer (e.g. after a login-as switch in the same browser) is discarded.
// Removed on logout.
// The list is kept in the order the actions were done (applied), not the order they were saved in - concurrent edits can be saved out
// of order, and replaying them in save order would show an older edit over a newer one. The order is stored with each action and never
// leaves elsa (not sent to the assistant).

const KEY = 'riseup.actionsDone';
// The redesign (elsa-v3, served under /v3 on the same origin) keeps its own list - it has no logout of its own, so it's removed here too.
// Must match KEY in elsa-v3/src/utils/actionsDone.ts (a near copy of this module - keep the two in sync).
const V3_KEY = 'riseup.actionsDone.v3';
const EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes - like the budget cache

// The actions the cashflow assistant can do (and apply) - only these are kept
export const ACTIONS_DONE_TYPES = ['changeTransactionCategory', 'moveFromTracking', 'changeCategory', 'changeEnvelopeExpenseCategory'];

// A change in the stored list - logged with the number of actions before and after it (action types and versions only, never params)
function _logChange(change, actionsBefore, actionsAfter, details = {}) {
  DDLogs.info('Actions done list changed', {
    change, numActionsBefore: actionsBefore.length, numActionsAfter: actionsAfter.length, ...details,
  });
}

// The signed-in customer - set by the session store. Without one, nothing is read or written.
let currentCustomerId = null;

export function setActionsDoneCustomer(customerId) {
  currentCustomerId = customerId ? String(customerId) : null;
}

// The customer an action is done for - taken when the action starts (before its save or the assistant's request), and passed to
// addActionsDone when it completes. Customers can be switched (login-as) without a reload while a save is pending: a completion of
// another customer's action is never recorded or applied.
export function actionsDoneCustomer() {
  return currentCustomerId;
}

// On logout: the lists (of this app and of the redesign) are removed, and nothing is read or written until a customer signs in
export function clearActionsDone() {
  currentCustomerId = null;
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(V3_KEY);
  } catch (error) {
    // Storage unavailable: nothing was stored
  }
}

let lastOrder = 0;

// The order of an action done - taken when the action is applied (before saving it), increasing within the session and across reloads
export function nextActionDoneOrder() {
  lastOrder = Math.max(Date.now(), lastOrder + 1);
  return lastOrder;
}

function _withoutOrder(actions) {
  return _.map(actions, action => _.omit(action, 'order'));
}

function _read() {
  if (!currentCustomerId) {
    return [];
  }
  try {
    const stored = JSON.parse(localStorage.getItem(KEY));
    // A partial or older entry may have no actions array (or malformed items) - read as no actions, never throwing to the callers (a
    // throw in a save's completion would revert an edit the server saved)
    const actions = Array.isArray(stored?.actions) ? _.filter(stored.actions, _.isPlainObject) : [];
    if (stored && stored.customerId !== currentCustomerId) {
      // Another customer's actions - never sent to the assistant or shown for this customer
      localStorage.removeItem(KEY);
      _logChange('other-customer', actions, []);
      return [];
    }
    // Also without a valid expiration time (a partial entry), so it can't stay forever
    if (stored && !(Date.now() <= stored.expirationTime)) {
      localStorage.removeItem(KEY);
      const expirationTime = Number.isFinite(stored.expirationTime) ? new Date(stored.expirationTime).toISOString() : null;
      _logChange('expired', actions, [], { expirationTime });
      return [];
    }
    return actions;
  } catch (error) {
    return [];
  }
}

function _save(actions) {
  if (!currentCustomerId) {
    return;
  }
  try {
    if (_.isEmpty(actions)) {
      localStorage.removeItem(KEY);
    } else {
      localStorage.setItem(KEY, JSON.stringify({ customerId: currentCustomerId, actions, expirationTime: Date.now() + EXPIRATION_TIME }));
    }
  } catch (error) {
    // Quota / serialization failures must not break the action
  }
}

// A budget's cashflow version: its `created` (or `createdAt` in budgets read from the adapted cashflows v2 collection, which have no
// `created`) - the same version the cashflow assistant uses
export function budgetVersion(budget) {
  return budget?.created || budget?.createdAt;
}

function _isOlderVersion(version, loadedVersion) {
  return !version || new Date(version) < new Date(loadedVersion);
}

// The actions done that the loaded cashflow version doesn't include yet - the ones done on an older version are dropped.
// Without a loaded version (the cashflow isn't loaded) nothing is dropped.
export function getActionsDone(loadedVersion) {
  const actions = _read();
  const notIncluded = loadedVersion ? _.reject(actions, action => _isOlderVersion(action.referenceVersion, loadedVersion)) : actions;
  if (notIncluded.length !== actions.length) {
    _save(notIncluded);
    // A newer cashflow version was loaded - it includes the actions done on older versions
    _logChange('drop-included-in-version', actions, notIncluded, {
      loadedVersion, droppedVersions: _.uniq(_.map(_.difference(actions, notIncluded), 'referenceVersion')),
    });
  }
  return _withoutOrder(notIncluded);
}

// Add actions done - an action without the version it was done on gets the given one (the version elsa shows). An action done on an
// older version than the given one (e.g. an assistant response on a version that elsa already replaced with a newer one) is already
// in the loaded cashflow, so it's not added - like on load. Returns the actions added, the ones to apply on the loaded cashflow.
// `order` is when the actions were applied (nextActionDoneOrder) - they are placed by it among the ones already added. Without it, they
// are placed as done now (e.g. the assistant's actions, when its response arrives). `customerId` is the customer the actions were done
// for (actionsDoneCustomer, taken when they started) - nothing is added if it's no longer the signed-in customer.
export function addActionsDone(actions, version, order = nextActionDoneOrder(), customerId = currentCustomerId) {
  if (!currentCustomerId || customerId !== currentCustomerId) {
    if (currentCustomerId) {
      _logChange('skip-other-customer', [], [], { numActionsSkipped: _.size(actions) });
    }
    return [];
  }
  const supported = _.chain(actions)
    // A supported action with params - a malformed one (e.g. in an assistant response) is never kept
    .filter(action => ACTIONS_DONE_TYPES.includes(action?.actionType) && _.isPlainObject(action.actionParams))
    .map(action => ({ ...action, referenceVersion: action.referenceVersion || version, order }))
    .value();
  const actionsToAdd = version ? _.reject(supported, action => _isOlderVersion(action.referenceVersion, version)) : supported;
  if (actionsToAdd.length !== supported.length) {
    _logChange('skip-included-in-version', supported, actionsToAdd, {
      loadedVersion: version, skippedVersions: _.uniq(_.map(_.difference(supported, actionsToAdd), 'referenceVersion')),
    });
  }
  if (!_.isEmpty(actionsToAdd)) {
    const actionsBefore = _read();
    // A stable sort - actions of the same order (one response) keep their order. Actions stored without an order come first.
    const actionsAfter = _.sortBy([...actionsBefore, ...actionsToAdd], action => action.order || 0);
    _save(actionsAfter);
    _logChange('add', actionsBefore, actionsAfter, {
      addedActionTypes: _.map(actionsToAdd, 'actionType'), addedVersions: _.uniq(_.map(actionsToAdd, 'referenceVersion')), loadedVersion: version,
    });
  }
  return _withoutOrder(actionsToAdd);
}

function _applyActionDone(cashflow, { actionType, actionParams }) {
  let params = actionParams;
  if (actionType === 'changeEnvelopeExpenseCategory') {
    // Envelope ids are per month - find the sequence's envelope in this month (never an envelope without a sequence)
    // (a stored action without params - saved before they were validated - is skipped)
    const envelope = actionParams?.sequenceId && _.find(cashflow.envelopes, e => e.details?.sequenceId === actionParams.sequenceId);
    if (!envelope) {
      return cashflow;
    }
    params = { ...actionParams, envelopeId: envelope.id };
  }
  try {
    return reactiveCashflow.optimus[actionType](cashflow, params, DateLocales.EN_IL);
  } catch (error) {
    // e.g. the transaction is not in this month
    DDLogs.info('Could not apply an action done on the cashflow', { actionType, error: error.message });
    return cashflow;
  }
}

// Show the actions done on a budget with reactive-cashflow (an action that doesn't apply to this month is skipped)
export function applyActionsDone(budget, actions) {
  return _.reduce(actions, _applyActionDone, budget);
}
